const TRACKING_PARAM = /^(utm_|fbclid|gclid|mc_|ref$|aff)/i
const SEARCH_THRESHOLD = 8

export const COMPARE_VIEWS = ['all', 'common', 'onlyA', 'onlyB']

function safeHttpUrl(raw) {
  const value = String(raw || '').trim()
  const lower = value.toLowerCase()
  if (!lower.startsWith('http://') && !lower.startsWith('https://')) return ''
  return value
}

export function canonicalProductUrl(raw) {
  const url = safeHttpUrl(raw)
  if (!url) return ''
  try {
    const parsed = new URL(url)
    parsed.hash = ''
    parsed.hostname = parsed.hostname.toLowerCase()
    parsed.pathname = parsed.pathname.replace(/\/+$/, '') || '/'
    const keys = [...parsed.searchParams.keys()]
    keys.forEach((key) => {
      if (TRACKING_PARAM.test(key)) parsed.searchParams.delete(key)
    })
    parsed.searchParams.sort()
    return parsed.toString()
  } catch {
    return ''
  }
}

export function externalImageKey(item) {
  const key = String(item?.image_key || '').trim()
  if (!key) return ''
  if (key.startsWith('data:') || key.startsWith('blob:')) return ''
  return key
}

export function itemMatchKey(item, { allowExternalImageKeyFallback = true } = {}) {
  const url = canonicalProductUrl(item?.link)
  if (url) return `url:${url}`
  if (!allowExternalImageKeyFallback) return null
  const imageKey = externalImageKey(item)
  if (imageKey) return `img:${imageKey}`
  return null
}

export function lineAmount(item) {
  const price = Number(item?.price) || 0
  const quantity = Number(item?.quantity) || 0
  return price * Math.max(quantity, 0)
}

export function cartTotal(items) {
  return (Array.isArray(items) ? items : []).reduce((sum, item) => sum + lineAmount(item), 0)
}

export function shouldShowCompareSearch(count) {
  return Number(count) >= SEARCH_THRESHOLD
}

export function filterCartsForPicker(carts, sourceCartId, query = '') {
  const needle = String(query || '').trim().toLowerCase()
  return (Array.isArray(carts) ? carts : [])
    .filter((cart) => cart?.id && cart.id !== sourceCartId)
    .filter((cart) => {
      if (!needle) return true
      return String(cart.name || '').toLowerCase().includes(needle)
    })
}

export function describeQtyGap(qtyA, qtyB) {
  const a = Number(qtyA) || 0
  const b = Number(qtyB) || 0
  if (a === b) return { type: 'same', label: `${a} unité(s) de chaque côté` }
  if ((a === 1 && b > 1) || (b === 1 && a > 1)) {
    return {
      type: 'unit-vs-refs',
      label: `Une unité d’un côté, ${Math.max(a, b)} exemplaires de l’autre — ce n’est pas le même nombre de références`,
    }
  }
  return {
    type: 'quantity',
    label: `Quantités différentes : ${a} contre ${b}`,
  }
}

export function orientedGapSentence(totalA, totalB, nameA, nameB) {
  const a = Number(totalA) || 0
  const b = Number(totalB) || 0
  const left = nameA || 'Panier A'
  const right = nameB || 'Panier B'
  const delta = Math.abs(a - b)
  const formatted = delta.toFixed(2).replace('.', ',')
  if (delta < 0.005) {
    return `Les deux paniers ont le même montant total (${formatted} € d’écart).`
  }
  if (a > b) return `${left} est plus élevé de ${formatted} €.`
  return `${right} est plus élevé de ${formatted} €.`
}

function metrics(items) {
  const list = Array.isArray(items) ? items : []
  return {
    total: cartTotal(list),
    references: list.length,
    units: list.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
  }
}

function makeRow({ kind, itemA = null, itemB = null, extra = {} }) {
  const quantityA = Number(itemA?.quantity) || 0
  const quantityB = Number(itemB?.quantity) || 0
  const unitPriceA = Number(itemA?.price) || 0
  const unitPriceB = Number(itemB?.price) || 0
  return {
    id: extra.id || `${kind}:${itemA?.id || 'none'}:${itemB?.id || 'none'}`,
    kind,
    itemA,
    itemB,
    unitPriceA,
    unitPriceB,
    quantityA,
    quantityB,
    subtotalA: lineAmount(itemA),
    subtotalB: lineAmount(itemB),
    priceChanged: kind === 'common' && Math.round(unitPriceA * 100) !== Math.round(unitPriceB * 100),
    quantityChanged: kind === 'common' && quantityA !== quantityB,
    qtyGap: kind === 'common' ? describeQtyGap(quantityA, quantityB) : null,
    matchKind: extra.matchKind || null,
  }
}

export function compareCarts(itemsA, itemsB, { allowExternalImageKeyFallback = false } = {}) {
  const listA = Array.isArray(itemsA) ? itemsA : []
  const listB = Array.isArray(itemsB) ? itemsB : []
  const bucketsA = new Map()
  const bucketsB = new Map()
  const unmatchedA = []
  const unmatchedB = []

  const place = (item, buckets, unmatched) => {
    const key = itemMatchKey(item, { allowExternalImageKeyFallback })
    if (!key) {
      unmatched.push(item)
      return
    }
    const group = buckets.get(key) || []
    group.push(item)
    buckets.set(key, group)
  }

  listA.forEach((item) => place(item, bucketsA, unmatchedA))
  listB.forEach((item) => place(item, bucketsB, unmatchedB))

  const common = []
  const onlyA = unmatchedA.map((item) => makeRow({ kind: 'onlyA', itemA: item }))
  const onlyB = unmatchedB.map((item) => makeRow({ kind: 'onlyB', itemB: item }))
  const collisions = []
  const keys = new Set([...bucketsA.keys(), ...bucketsB.keys()])

  keys.forEach((key) => {
    const groupA = [...(bucketsA.get(key) || [])]
    const groupB = [...(bucketsB.get(key) || [])]
    if (groupA.length > 1 || groupB.length > 1) {
      collisions.push({ key, countA: groupA.length, countB: groupB.length })
    }
    if (groupA.length === 1 && groupB.length === 1) {
      common.push(makeRow({
        kind: 'common',
        itemA: groupA[0],
        itemB: groupB[0],
        extra: { matchKind: key.startsWith('url:') ? 'url' : 'image' },
      }))
      return
    }
    groupA.forEach((item) => onlyA.push(makeRow({ kind: 'onlyA', itemA: item })))
    groupB.forEach((item) => onlyB.push(makeRow({ kind: 'onlyB', itemB: item })))
  })

  const metricsA = metrics(listA)
  const metricsB = metrics(listB)
  const rows = [...common, ...onlyA, ...onlyB]

  return {
    rows,
    common,
    onlyA,
    onlyB,
    collisions,
    metricsA,
    metricsB,
    totalA: metricsA.total,
    totalB: metricsB.total,
    totalDifference: Math.round((metricsB.total - metricsA.total) * 100) / 100,
    matchRule:
      'Les produits équivalents sont appariés par URL produit normalisée. Le nom seul ne suffit jamais. Une clé d’image externe identique n’est utilisée qu’en secours explicite.',
    amountVsMatch:
      'Le montant total compare la somme des lignes. L’équivalence produit ne porte que sur les lignes appariées par lien, éventuellement par image.',
  }
}

export function buildCartComparison(itemsA, itemsB, metaA = {}, metaB = {}, options = {}) {
  const comparison = compareCarts(itemsA, itemsB, {
    allowExternalImageKeyFallback: options.allowExternalImageKeyFallback !== false,
  })
  return {
    ...comparison,
    pairs: comparison.common.map((row) => ({
      id: row.id,
      a: row.itemA,
      b: row.itemB,
      matchKind: row.matchKind,
      priceDelta: Math.round((row.unitPriceB - row.unitPriceA) * 100) / 100,
      qtyDelta: row.quantityB - row.quantityA,
      qtyGap: row.qtyGap,
      samePrice: !row.priceChanged,
    })),
    onlyA: comparison.onlyA.map((row) => row.itemA),
    onlyB: comparison.onlyB.map((row) => row.itemB),
    countA: comparison.metricsA.references,
    countB: comparison.metricsB.references,
    gap: comparison.totalDifference,
    orientedSentence: orientedGapSentence(comparison.totalA, comparison.totalB, metaA.name, metaB.name),
  }
}

export function rowsForView(comparison, view = 'all') {
  const data = comparison || { rows: [], common: [], onlyA: [], onlyB: [] }
  if (view === 'common') return data.common || []
  if (view === 'onlyA') return data.onlyA || []
  if (view === 'onlyB') return data.onlyB || []
  return data.rows || []
}
